import { Container } from '@pixi/display';
import { Graphics } from '@pixi/graphics';
import { Sprite } from '@pixi/sprite';
import { Text } from '@pixi/text';
import gsap from 'gsap';

import { Shared } from '../App';
import { RankStatus } from '../plugins/RankPlugin';
import { Maths } from '../utils/Maths';

export class RankBadge extends Container
{
    private circle: Sprite;
    private borderEmpty: Sprite;
    private borderFull: Sprite;
    private borderMask: Graphics;
    private image: Sprite;
    private tier: Text;
    private _size: number;
    private _rankProgress: number;

    constructor(size = 160)
    {
        super();

        this._size = size;
        this._rankProgress = 0;

        this.circle = new Sprite(Shared.assets.getTexture('item-holder-large'));
        this.circle.anchor.set(0.5);
        this.addChild(this.circle);
        this.circle.width = size;
        this.circle.scale.y = this.circle.scale.x;

        this.image = new Sprite(Shared.assets.getTexture('gold-medal'));
        this.image.anchor.set(0.5);
        this.addChild(this.image);
        this.image.width = size * 1.3;
        this.image.scale.y = this.image.scale.x;

        this.borderEmpty = new Sprite(Shared.assets.getTexture('item-holder-large-border'));
        this.borderEmpty.anchor.set(0.5);
        this.addChild(this.borderEmpty);
        this.borderEmpty.width = size * 1.17;
        this.borderEmpty.scale.y = this.borderEmpty.scale.x;
        this.borderEmpty.tint = 0x1A2B33;

        this.borderFull = new Sprite(Shared.assets.getTexture('item-holder-large-border'));
        this.borderFull.anchor.set(0.5);
        this.addChild(this.borderFull);
        this.borderFull.width = this.borderEmpty.width;
        this.borderFull.scale.y = this.borderFull.scale.x;
        this.borderFull.tint = 0x73C5F1;

        this.borderMask = new Graphics();
        this.addChild(this.borderMask);
        this.borderFull.mask = this.borderMask;

        this.tier = new Text(`III`, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 60,
            lineJoin: 'round',
            stroke: 'black',
            strokeThickness: 8,
        });
        this.tier.anchor.set(0.5);
        this.addChild(this.tier);
        this.tier.y = this._size * 0.11;
    }

    public get size(): number
    {
        return this._size;
    }

    public refresh(status: RankStatus, animDuration = 0): void
    {
        const title = status.title;
        let tier = status.tier;

        gsap.killTweensOf(this);

        if (!animDuration || status.progress <= this.rankProgress)
        {
            this.rankProgress = status.progress;
        }
        else
        {
            gsap.to(this, { rankProgress: status.progress, duration: animDuration, ease: 'linear' });
        }

        this.image.texture = Shared.assets.getTexture(`${title}-medal`);

        this.swapMedal(title, animDuration > 0);

        if (tier < 1) tier = 1;
        let tierText = '';

        while (tier--) tierText += 'I';

        this.swapTier(tierText, animDuration > 0);
    }

    protected get rankProgress(): number
    {
        return this._rankProgress;
    }

    protected set rankProgress(v: number)
    {
        this._rankProgress = Maths.clamp(v);
        this.updateMask(v);
    }

    private updateMask(ratio: number): void
    {
        const angle = Maths.PI_DOUBLE * ratio;

        this.borderMask.clear();
        this.borderMask.beginFill(0xeeeeee);
        this.borderMask.moveTo(0, 0);
        this.borderMask.arc(0, 0, this._size * 0.6, 0, angle, false);
        this.borderMask.rotation = -Maths.PI_HALF;
    }

    private async swapMedal(title: string, animated = false): Promise<void>
    {
        const texture = Shared.assets.getTexture(`${title}-medal`);

        if (this.image.texture === texture) return;
        gsap.killTweensOf(this.image);
        this.image.alpha = 1;

        if (!animated)
        {
            this.image.texture = texture;
        }
        else
        {
            await gsap.to(this.image, { alpha: 0, duration: 0.1, ease: 'linear' });
            this.image.texture = texture;
            gsap.to(this.image, { alpha: 1, duration: 0.2, ease: 'linear' });
        }
    }

    private async swapTier(tierText: string, animated = false): Promise<void>
    {
        if (this.tier.text === tierText) return;
        gsap.killTweensOf(this.tier);
        gsap.killTweensOf(this.tier.scale);
        this.tier.alpha = 1;

        if (!animated)
        {
            this.tier.text = tierText;
        }
        else
        {
            await gsap.to(this.tier, { alpha: 0, duration: 0.1, ease: 'linear' });
            this.tier.text = tierText;
            this.tier.scale.set(2);
            gsap.to(this.tier, { alpha: 1, duration: 0.2, ease: 'linear' });
            gsap.to(this.tier.scale, { x: 1, y: 1, duration: 0.5, ease: 'bounce.out' });
        }
    }
}
